import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Checkbox } from '@ics-portal/guardian';
import { Icon } from '../shared/Icon';
import type { ExpandConfig, SelectConfig } from './TableView';

export interface SelectHeaderProps {
    state: 'none' | 'some' | 'all';
    onToggle: () => void;
}

const indeterminateRef =
    (value: boolean) =>
    (element: HTMLInputElement | null): void => {
        if (element) element.indeterminate = value;
    };

export function SelectHeader(props: SelectHeaderProps): JSX.Element {
    const { state, onToggle } = props;

    return (
        <span className="ics-table__select-wrap">
            <input
                type="checkbox"
                className="ics-table__select-cell"
                checked={state === 'all'}
                aria-checked={state === 'some' ? 'mixed' : undefined}
                onChange={onToggle}
                aria-label="Select all rows"
                ref={indeterminateRef(state === 'some')}
            />
        </span>
    );
}

export interface SelectCellProps {
    mode: 'single' | 'multi';
    selected: boolean;
    disabled?: boolean;
    label: string;
    onToggle: () => void;
}

export function SelectCell(props: SelectCellProps): JSX.Element {
    const { mode, selected, disabled, label, onToggle } = props;

    if (mode === 'single') {
        return (
            <input
                type="radio"
                checked={selected}
                disabled={disabled}
                onChange={onToggle}
                aria-label={label}
                className="ics-table__select-cell"
            />
        );
    }

    return (
        <span className="ics-table__select-wrap">
            <Checkbox
                checked={selected}
                disabled={disabled}
                onChange={onToggle}
                aria-label={label}
            />
        </span>
    );
}

export interface ExpandCellProps {
    expanded: boolean;
    enabled: boolean;
    count?: number;
    label: string;
    onToggle: () => void;
}

export function ExpandCell(props: ExpandCellProps): JSX.Element {
    const { expanded, enabled, count, label, onToggle } = props;
    const chevronClassName = expanded
        ? 'ics-table__expand-chevron ics-table__expand-chevron--expanded'
        : 'ics-table__expand-chevron';

    if (!enabled) {
        return (
            <span className="ics-table__expand-toggle ics-table__expand-toggle--empty" />
        );
    }

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={label}
            className="ics-table__expand-toggle"
        >
            {}
            <span className={chevronClassName}>
                <Icon name="keyboard_arrow_down" />
            </span>
            {count === undefined ? null : (
                <span className="ics-table__expand-count">{count}</span>
            )}
        </button>
    );
}

export interface DragCellProps {
    label: string;
    onDragStart: () => void;
    onDragEnd: () => void;
    onMove: (dir: -1 | 1) => void;
}

export function DragCell(props: DragCellProps): JSX.Element {
    const { label, onDragStart, onDragEnd, onMove } = props;

    return (
        <button
            type="button"
            className="ics-table__row-drag"
            aria-label={label}
            draggable
            onDragEnd={onDragEnd}
            onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', label);
                const row = e.currentTarget.closest('tr');
                if (row && typeof e.dataTransfer.setDragImage === 'function') {
                    const rect = row.getBoundingClientRect();
                    e.dataTransfer.setDragImage(
                        row,
                        e.clientX - rect.left,
                        e.clientY - rect.top,
                    );
                }
                onDragStart();
            }}
            onKeyDown={(e) => {
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    onMove(-1);
                }
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    onMove(1);
                }
            }}
        >
            <span
                aria-hidden="true"
                className="guardian-icon guardian-icon--small"
            >
                drag_indicator
            </span>
        </button>
    );
}

export interface MenuCellProps {
    children?: ReactNode;
}

export function MenuCell(props: MenuCellProps): JSX.Element | null {
    const { children } = props;
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event: MouseEvent): void => {
            if (!ref.current?.contains(event.target as Node)) setOpen(false);
        };
        const onClick = (event: MouseEvent): void => {
            const target = event.target as HTMLElement;
            if (target.closest('[role="menuitem"]')) setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('click', onClick);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('click', onClick);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    if (!children) {
        return null;
    }

    return (
        <div ref={ref} className="ics-table__menu-cell">
            <button
                type="button"
                aria-label="Row actions"
                aria-haspopup="menu"
                aria-expanded={open}
                className="ics-table__menu-trigger"
                onClick={() => {
                    setOpen((current) => !current);
                }}
            >
                <Icon name="more_vert" />
            </button>
            {open ? (
                <div className="ics-table__row-menu-anchor">{children}</div>
            ) : null}
        </div>
    );
}

export function leadingCell<T>(
    row: T,
    id: string,
    select?: SelectConfig<T>,
    expand?: ExpandConfig<T>,
): ReactNode {
    if (!select && !expand) {
        return null;
    }
    return (
        <>
            {select ? (
                <td className="ics-table__cell ics-table__cell--select">
                    <SelectCell
                        mode={select.mode}
                        selected={select.isSelected(row)}
                        disabled={!select.canSelect(row)}
                        label={`Select row ${id}`}
                        onToggle={() => {
                            select.onToggle(row);
                        }}
                    />
                </td>
            ) : null}
            {expand ? (
                <td className="ics-table__cell ics-table__cell--expand">
                    <ExpandCell
                        expanded={expand.isExpanded(row)}
                        enabled={expand.enabled(row)}
                        count={expand.count?.(row)}
                        label={`Expand row ${id}`}
                        onToggle={() => {
                            expand.onToggle(row);
                        }}
                    />
                </td>
            ) : null}
        </>
    );
}
